import React, { useEffect, useState, useCallback } from "react";
import { orgAdmin } from "../../api";
import { useAuth } from "../../context/AuthContext";
import {
  PageHeader, Btn, Badge, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

export default function OrgStaffPage() {
  const { user }   = useAuth();
  const orgId      = user?.organisationId;

  const [staff,    setStaff]    = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState("");
  const [branchFilter, setBranchFilter] = useState("all");

  const [transferModal, setTransferModal] = useState(null); // staff user
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [sf, br] = await Promise.all([
        orgAdmin.listOrgStaff(),
        orgAdmin.getBranches(orgId),
      ]);
      setStaff(sf.data?.results || sf.data || []);
      setBranches(br.data?.results || br.data || []);
    } catch { toast.error("Failed to load staff."); }
    finally  { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  const filtered = staff.filter(u => {
    const matchSearch = !search ||
      u.name?.toLowerCase().includes(search.toLowerCase()) ||
      u.email?.toLowerCase().includes(search.toLowerCase()) ||
      u.facility_name?.toLowerCase().includes(search.toLowerCase());
    const matchBranch = branchFilter === "all" || u.facility === branchFilter;
    return matchSearch && matchBranch;
  });

  const handleTransfer = async (targetBranchId) => {
    setSaving(true);
    try {
      const res = await orgAdmin.transferStaff(transferModal.id, { facility_id: targetBranchId });
      toast.success(res.data.message);
      setTransferModal(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.error || "Transfer failed.");
    } finally { setSaving(false); }
  };

  const roleColor = (role) => {
    if (role === "facility_admin") return { bg: "rgba(96,165,250,0.12)", color: "#60A5FA", border: "rgba(96,165,250,0.25)" };
    return { bg: "rgba(34,197,94,0.1)", color: "#22C55E", border: "rgba(34,197,94,0.2)" };
  };

  if (loading) return <div style={{ padding: "32px" }}><LoadingSpinner /></div>;

  return (
    <div style={styles.root}>
      <PageHeader
        title="Staff Directory"
        subtitle={`${staff.length} staff member${staff.length !== 1 ? "s" : ""} across all branches`}
      />

      <div style={styles.toolbar}>
        <input
          style={styles.search}
          placeholder="Search by name, email, branch…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          style={styles.select}
          value={branchFilter}
          onChange={e => setBranchFilter(e.target.value)}
        >
          <option value="all">All Branches</option>
          {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon="👤" title="No staff found" subtitle={search ? "Try a different search." : "No staff in your branches yet."} />
      ) : (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead><tr>
              {["Staff Member", "Email", "Branch", "Station", "Role", "Status", "Actions"].map(h =>
                <th key={h} style={styles.th}>{h}</th>
              )}
            </tr></thead>
            <tbody>
              {filtered.map(u => {
                const rc = roleColor(u.role);
                return (
                  <tr key={u.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <div style={styles.avatar}>{u.name?.[0]?.toUpperCase() || "?"}</div>
                        <span style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>{u.name}</span>
                      </div>
                    </td>
                    <td style={{ ...styles.td, color: "var(--text-secondary)", fontSize: "13px" }}>{u.email}</td>
                    <td style={{ ...styles.td, fontSize: "13px", color: "var(--text-secondary)" }}>
                      {u.facility_name || "—"}
                    </td>
                    <td style={{ ...styles.td, color: "var(--text-secondary)", fontSize: "13px" }}>
                      {u.station_name || "Unassigned"}
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: "11px", padding: "2px 8px", borderRadius: "10px", background: rc.bg, color: rc.color, border: `1px solid ${rc.border}`, fontWeight: "600" }}>
                        {u.role === "facility_admin" ? "Branch Admin" : "Staff"}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <Badge value={u.is_active ? "active" : "inactive"} />
                    </td>
                    <td style={styles.td}>
                      <Btn
                        size="sm"
                        variant="ghost"
                        onClick={() => setTransferModal(u)}
                        title="Transfer to another branch"
                      >
                        Transfer →
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Transfer Modal */}
      <Modal
        open={!!transferModal}
        onClose={() => setTransferModal(null)}
        title="Transfer Staff Member"
        width="440px"
      >
        {transferModal && (
          <TransferForm
            staff={transferModal}
            branches={branches}
            onTransfer={handleTransfer}
            onClose={() => setTransferModal(null)}
            saving={saving}
          />
        )}
      </Modal>
    </div>
  );
}

function TransferForm({ staff, branches, onTransfer, onClose, saving }) {
  const [targetBranch, setTargetBranch] = useState("");
  const currentBranchId = staff.facility;

  const eligibleBranches = branches.filter(b => b.id !== currentBranchId && b.status !== "suspended");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <div style={styles.staffCard}>
        <div style={styles.avatar}>{staff.name?.[0]?.toUpperCase() || "?"}</div>
        <div>
          <div style={{ fontSize: "14px", fontWeight: "600", color: "var(--text-primary)" }}>{staff.name}</div>
          <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>{staff.email}</div>
          <div style={{ fontSize: "12px", color: "var(--text-secondary)", marginTop: "2px" }}>
            Currently at: <strong style={{ color: "var(--text-primary)" }}>{staff.facility_name || "Unassigned"}</strong>
            {staff.station_name && ` · ${staff.station_name}`}
          </div>
        </div>
      </div>

      <div style={{ background: "rgba(234,179,8,0.06)", border: "1px solid rgba(234,179,8,0.2)", borderRadius: "8px", padding: "10px 14px", fontSize: "12px", color: "#EAB308" }}>
        ⚠ Transferring will remove their current station assignment. They'll need to be reassigned at the new branch.
      </div>

      <FormField label="Transfer To" required>
        <select style={selectStyle} value={targetBranch} onChange={e => setTargetBranch(e.target.value)}>
          <option value="">Select destination branch…</option>
          {eligibleBranches.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      </FormField>

      {eligibleBranches.length === 0 && (
        <div style={{ color: "#EF4444", fontSize: "13px" }}>No other active branches available for transfer.</div>
      )}

      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn
          onClick={() => onTransfer(targetBranch)}
          disabled={saving || !targetBranch}
        >
          {saving ? "Transferring…" : "Confirm Transfer"}
        </Btn>
      </div>
    </div>
  );
}

const styles = {
  root:      { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "1100px" },
  toolbar:   { display: "flex", gap: "12px", marginBottom: "20px", flexWrap: "wrap" },
  search:    { background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "8px", padding: "9px 14px", color: "var(--text-primary)", fontSize: "14px", outline: "none", width: "300px", fontFamily: "inherit" },
  select:    { background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "8px", padding: "9px 14px", color: "var(--text-primary)", fontSize: "14px", outline: "none", fontFamily: "inherit" },
  tableWrap: { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "14px", overflow: "hidden" },
  table:     { width: "100%", borderCollapse: "collapse" },
  th:        { padding: "12px 16px", fontSize: "11px", fontWeight: "700", color: "var(--text-muted)", letterSpacing: "0.07em", textTransform: "uppercase", textAlign: "left", borderBottom: "1px solid var(--surface-border)", background: "var(--table-header-bg)" },
  tr:        { borderBottom: "1px solid rgba(37,99,235,0.06)" },
  td:        { padding: "12px 16px", verticalAlign: "middle" },
  avatar:    { width: "32px", height: "32px", borderRadius: "8px", background: "linear-gradient(135deg,#2563EB,#7C3AED)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700", color: "#fff", flexShrink: 0 },
  staffCard: { display: "flex", gap: "12px", alignItems: "center", background: "var(--input-bg)", border: "1px solid var(--surface-border)", borderRadius: "10px", padding: "12px 16px" },
};
